import React, { FC } from 'react';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import Stack from '../stack/Stack';
import Html from '../html/Html';
import { useBackgroundColor, useResponsiveType } from '../../utils';
import { ThemeColorsKeys } from '../../styles/tokens/colors';
import { colors } from '../../styles/tokens';

// Interface

interface ProcessProps {
  items: ProcessItem[];
}

interface ProcessItem {
  components?: React.ReactNode;
  backgroundColor?: ThemeColorsKeys;
}

interface StyledProcessItemProps {
  backgroundColor: ThemeColorsKeys;
}

// Styles

export const StyledProcess = styled(Stack)`
  ${({ theme: { space, borderRadii } }) => css`
    counter-reset: process;
  `};
`;

const StyledProcessItem = styled.li<StyledProcessItemProps>`
  ${({ theme: { space, borderRadii, media, layers }, backgroundColor }) => css`
    ${useBackgroundColor(backgroundColor)}
    border-radius: ${borderRadii.small};
    border: solid 1px ${colors.darkBlue};
    padding: ${space.small};
    counter-increment: process;
    display: flex;
    flex-direction: column;
    gap: ${space.medium};
    position: relative;

    &:after {
      content: '';
      display: block;
      width: 1px;
      height: calc(${space.xLarge} + ${space.small} + 2px);
      position: absolute;
      top: 100%;
      left: calc(${space.small} + ${space.small} - 2px);
      background-color: ${colors.darkBlue};
      z-index: ${layers.processConnector};
      flex-shrink: 0;
      flex-grow: 0;
    }

    &:before {
      content: counter(process);
      ${useBackgroundColor('darkBlue')}
      ${useResponsiveType('headingSmall')}
      width: ${space.large};
      height: ${space.large};
      border-radius: 50%;
      display: grid;
      place-items: center;
      flex-shrink: 0;
      flex-grow: 0;
    }

    &:last-of-type {
      &:after {
        display: none;
      }
    }

    @media (min-width: ${media.medium}) {
      flex-direction: row;
      gap: ${space.large};
      padding: ${space.large};

      &:after {
        height: calc(${space.xLarge} + ${space.large} + 2px);
        left: calc(${space.large} + 34px);
      }

      &:before {
        width: ${space.xxLarge};
        height: ${space.xxLarge};
      }
    }
  `};
`;

// ProcessItem Component
const ProcessItem: FC<ProcessItem> = ({
  components,
  backgroundColor = 'neutral',
}) => (
  <StyledProcessItem backgroundColor={backgroundColor}>
    {components && <Stack>{components}</Stack>}
  </StyledProcessItem>
);

// Process Component
export const Process: FC<ProcessProps> = ({ items, ...rest }) => (
  <StyledProcess gap="xLarge" as="ol" {...rest}>
    {items?.map((item, index) => (
      <ProcessItem key={index} {...item} />
    ))}
  </StyledProcess>
);

export default Process;
